/* eslint-disable operator-linebreak, no-undef */
/**
 * The main js file for the theme
 *
 * This is the entry point where all modules are imported and compiled.
 *
 * @package Embark
 * @since   1.0.0
 */

import FontFaceObserver from 'fontfaceobserver';
import { Breakpoints } from './theme/breakpoints';
import { scrollToHash } from './theme/helpers';
import { hasFunctionalConsent } from './theme/consent';
import {
	imageLinkQuerySelector,
	videoLinkQuerySelector,
	youtubeRegex,
	youtubePageRegex,
} from './theme/lightboxes/helpers';

class Embark {
	// Object to store components in order to expose them to other components.
	components = {};

	// Object to store the focus trap element and callback.
	focusTrap = {
		el: null,
		callback: null,
	};

	// Array of events that should trigger delayed imports.
	interactionEvents = [
		'click',
		'mousedown',
		'mousemove',
		'keydown',
		'touchstart',
		'keydown',
		'scroll',
		'pointerdown',
	];

	// Flag to prevent multiple imports of delayed modules.
	isImportingDelayedJS = false;

	/**
	 * Set variables for common elements, fonts, breakpoints and dynamically
	 * import all modules for the project.
	 */
	constructor() {
		/**
		 * Common elements used throughout the project.
		 */
		this.els = {
			html: document.documentElement,
			body: document.body,
			header: document.getElementById('js-site-header'),
			navContainer: document.querySelector('.site-header__navigation'),
			mainMenu: document.querySelector('.main-menu'),
			navicon: document.getElementById('js-navicon'),
			naviconTrigger: document.getElementById('js-navicon-trigger'),
			utilities: document.querySelector('.utilities'),
			anchor: document.querySelector('.top-anchor'),
		};

		/**
		 * Each object in this array has the following parameters:
		 *
		 * path: The filepath, without file extension, to import relative to "./theme/".
		 * test: A test to check if the module should be imported or not. Typically,
		 * this is simply checking to see if an element exists in the DOM.
		 */
		this.imports = [
			{
				path: 'skip-to-content',
				test: true,
			},
			{
				path: 'sticky-header',
				test: this.els.header,
			},
			{
				path: 'main-menu',
				test: this.els.mainMenu,
				delay: false,
			},
			{
				path: 'top-anchor',
				test: this.els.anchor,
			},
			{
				path: 'forms/file-input',
				test: document.querySelector('input[type="file"]'),
				delay: false,
			},
			{
				path: 'forms/ios-select-fix',
				test: document.querySelector('.ios select'),
			},
			{
				path: 'forms/gf-tabindex-fix',
				test: document.querySelector(
					'.ginput_container_consent [type="checkbox"][tabindex], .gform_button[tabindex]',
				),
			},
			{
				path: 'forms/gf-validation',
				test: document.querySelector('.gform_validation_error'),
				delay: false,
			},
			{
				path: 'comment-reply',
				test: document.querySelector('#reply-title'),
			},
			{
				path: 'pdf-links',
				test: true,
			},
			{
				path: 'accordions',
				test: document.querySelector(
					'.em-panels--accordions, .em-block-accordion',
				),
			},
			{
				path: 'ajax-load',
				test: document.querySelector('.js-load-more-button'),
			},
			{
				path: 'block-spotlight-slider',
				test: document.querySelector('.block--spotlight-slider'),
			},
			{
				path: 'blog',
				test: document.querySelector(
					'.blog, .locations__categories, .cat-dropdown',
				),
				delay: false,
			},
			{
				path: 'brightcove-consent',
				test: document.querySelector('video-js[data-account]'),
				delay: false,
			},
			{
				path: 'breadcrumbs',
				test: document.querySelector('.breadcrumbs__dropdown'),
			},
			{
				path: 'content-slider',
				test: document.querySelector('.content-slider'),
			},
			{
				path: 'countup-block',
				test: document.querySelector('.countup-block__number'),
				delay: false,
			},
			{
				path: 'covid-modal',
				test: document.querySelector('div[class="covid-modal"]'),
			},
			{
				path: 'cta-anchor',
				test: document.querySelector('.cta-anchor'),
			},
			{
				path: 'discover-more',
				test: document.querySelector(
					'.discover-more, .blog-nav__mobile-discover, .discover-more-popup--close, .discover-more-popup--overlay',
				),
			},
			{
				path: 'em-glossary',
				test: document.querySelector(`a[href*="/${'glossary-term'}/"]`),
			},
			{
				path: 'hero-photo-gallery',
				test: document.querySelector('.hero-photo-gallery'),
			},
			{
				path: 'hero-slider',
				test: document.querySelector('.hero-slider'),
				delay: false,
			},
			{
				path: 'hero-title',
				test: document.querySelector('.single-hero'),
			},
			{
				path: 'hex-nav',
				test: document.querySelector('.sc-hex, .sc-ppd, .sc-hex-filled'),
			},
			{
				path: 'hp-hero-slider',
				test: document.querySelector('.hp-hero-slider'),
				delay: false,
			},
			{
				path: 'image-compare',
				test: document.querySelector('[class*="twentytwenty]'),
			},
			{
				path: 'insights-hub',
				test:
					document.querySelector('.tmpl-insights-hub-archive') ||
					document.location.pathname === '/insights-hub/',
				delay: false,
			},
			{
				path: 'masthead-slider',
				test: document.querySelector('.masthead'),
			},
			{
				path: 'news-releases',
				test: document.location.pathname === '/ppd-news-releases/',
			},
			{
				path: 'panel-slider',
				test: document.querySelector('.panel-slider'),
			},
			{
				path: 'quote-slider',
				test: document.querySelector('.quote-slider'),
			},
			{
				path: 'resources',
				test: document.querySelector(
					'.resource-post__body, .registration__content',
				),
			},
			{
				path: 'block-flip-card',
				test: document.querySelector('.em-flip-card-container'),
				delay: false,
			},
			{
				path: 'search',
				test: true,
			},
			{
				path: 'lightboxes',
				test: (() => {
					const els = [
						...document.querySelectorAll(
							`${imageLinkQuerySelector}, ${videoLinkQuerySelector}, .em-filterable-content`,
						),
					];
					if (els.length) {
						const filtered = els.filter((el) => {
							// Don't initialize lightboxes for YouTube profile or channel links.
							if (
								el.nodeName === 'A' &&
								el.href.match(youtubeRegex) &&
								el.href.match(youtubePageRegex)
							) {
								return false;
							}

							return true;
						});

						return filtered.length > 0;
					}

					return false;
				})(),
			},
		];

		/**
		 * Array of font-family names to observe with FontFaceObserver to ensure they load.
		 */
		this.fonts = [];

		/**
		 * Breakpoints used throughout the project. These should match up with
		 * the breakpoints defined in src/scss/config/mixins/_media-queries.scss.
		 */
		this.breakpoints = new Breakpoints([
			{
				key: 'xxs',
				value: 320,
			},
			{
				key: 'xs',
				value: 480,
			},
			{
				key: 'sm',
				value: 768,
			},
			{
				key: 'md',
				value: 1025,
			},
			{
				key: 'lg',
				value: 1200,
			},
			{
				key: 'menu',
				value: 1000,
			},
		]);

		this.init();
	}

	init = async () => {
		// this.loadFonts();
		this.loadJS();
		this.makeImagesAccessible();

		window.addEventListener('load', scrollToHash);
		window.addEventListener('hashchange', scrollToHash);
	};

	/**
	 * Watch as fonts are loaded and add a class to the html element when they are.
	 * Then set a cookie so that on subsequent page views, head.js can quickly add
	 * the class, avoiding FOUT.
	 *
	 * @since 2.1.1
	 */
	loadFonts = () => {
		if (!this.els.html.classList.contains('fonts-loaded')) {
			Promise.all(
				this.fonts.map((font) => new FontFaceObserver(font).load()),
			).then(() => {
				this.els.html.classList.add('fonts-loaded');
				// Persist the fonts-loaded flag only with functional consent.
				if (hasFunctionalConsent()) {
					document.cookie = 'emFontsLoaded=true; SameSite=None; Secure';
				}
			});
		}
	};

	/**
	 * Load a module by path.
	 *
	 * @param {string} path The path to the module.
	 */
	loadModule = (path) => {
		import(
			/* webpackChunkName: "[request]" */
			`./theme/${path}`
		).catch((error) => {
			console.error(`Error loading: ${path}`, error);
		});
	};

	/**
	 * Load modules that are needed on the current page.
	 */
	loadJS = () => {
		this.imports.forEach(({ path, test, delay }) => {
			if (delay === false && test) {
				this.loadModule(path);
			}
		});

		// Load delayed JS after user interaction.
		this.interactionEvents.forEach((event) => {
			document.addEventListener(event, this.loadDelayedJS, { once: true });
		});
	};

	/**
	 * Load delayed JS after user interaction.
	 */
	loadDelayedJS = () => {
		if (this.isImportingDelayedJS) {
			return;
		}

		// Prevent multiple imports of delayed modules.
		this.isImportingDelayedJS = true;

		// Import modules that are delayed.
		this.imports.forEach(({ path, test, delay }) => {
			if (delay !== false && test) {
				this.loadModule(path);
			}
		});

		// Remove event listeners.
		this.interactionEvents.forEach((event) => {
			document.removeEventListener(event, this.loadDelayedJS);
		});
	};

	/**
	 * Add empty alt attributes to images that don't have them and set aria-hidden to true.
	 */
	makeImagesAccessible = () => {
		const noAltImages = [
			...document.querySelectorAll('img:not([alt]), img[alt=""]'),
		];
		if (noAltImages.length) {
			noAltImages.forEach((img) => {
				img.setAttribute('alt', '');
				img.setAttribute('aria-hidden', 'true');
			});
		}
	};
}

document.addEventListener('DOMContentLoaded', () => {
	window.embark = new Embark();
});
